import { useEffect, useState } from "react";
import { BLOCK_PRESETS, type BlockPresetId } from "@/config/designer";
import { clearDesign, loadSummary, type SavedDesignSummary } from "@/core/persistence/storage";
import { useDesignerStore } from "@/state/designerStore";
import { BlockFigure } from "./Icons";
import { EthosLogo } from "./EthosLogo";

interface SetupScreenProps {
  onStart: (presetId: BlockPresetId) => void;
  onContinue: () => void;
}

export function SetupScreen({ onStart, onContinue }: SetupScreenProps): React.ReactElement {
  const presetId = useDesignerStore((s) => s.presetId);
  const setPreset = useDesignerStore((s) => s.setPreset);
  const [saved, setSaved] = useState<SavedDesignSummary | null>(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    let live = true;
    void loadSummary().then((summary) => {
      if (!live) return;
      setSaved(summary);
      setChecked(true);
    });
    return () => {
      live = false;
    };
  }, []);

  const discard = async (): Promise<void> => {
    await clearDesign();
    setSaved(null);
  };

  return (
    <main className="setup">
      <div className="setup__inner">
        <header className="setup__head">
          <EthosLogo className="ethos-logo--setup" />
          <p className="eyebrow">ETHOS Climbing</p>
          <h1 className="display">Hold Shaper V1.1</h1>
          <p>Choose your foam block and start shaping.</p>
        </header>

        <ul className="presets">
          {BLOCK_PRESETS.map((preset) => {
            const selected = preset.id === presetId;
            return (
              <li key={preset.id}>
                <button
                  type="button"
                  className="preset"
                  aria-pressed={selected}
                  onClick={() => setPreset(preset.id)}
                  onDoubleClick={() => onStart(preset.id)}
                >
                  <span className="preset__figure">
                    <BlockFigure
                      width={preset.width}
                      depth={preset.depth}
                      height={preset.height}
                      scale={78 / Math.max(preset.width, preset.depth)}
                    />
                  </span>
                  <span className="preset__name">{preset.label}</span>
                  <span className="preset__dims">
                    {preset.width} × {preset.depth} × {preset.height} mm
                  </span>
                  <span className="preset__blurb">{preset.blurb}</span>
                </button>
              </li>
            );
          })}
        </ul>

        <div className="setup__actions">
          <button type="button" className="btn btn--primary" onClick={() => onStart(presetId)}>
            Start shaping
          </button>
          {saved ? (
            <>
              <button type="button" className="btn" onClick={onContinue}>
                Continue design
              </button>
              <button type="button" className="btn btn--ghost" onClick={() => void discard()}>
                Discard saved
              </button>
            </>
          ) : null}
        </div>

        <p className="setup__foot">
          {checked && saved
            ? `Saved design: ${saved.width} × ${saved.depth} × ${saved.height} mm, ${formatWhen(saved.savedAt)}. `
            : ""}
          Your design stays in your browser. No account, no upload.
        </p>
      </div>
    </main>
  );
}

function formatWhen(timestamp: number): string {
  const minutes = Math.round((Date.now() - timestamp) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
}
